import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import ConfirmDialog from '../ui/ConfirmDialog';
import Modal from '../ui/Modal';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { downloadDocument, verifyDocument, unverifyDocument, deleteDocument } from '../../api/documents';
import { pushToast } from '../ui/toastBus';
import { saveBlobResponse } from '../../utils/download';
import DocumentUploadModal from './DocumentUploadModal';

function formatSize(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

// Browsers can't decode HEIC/HEIF natively, so even though their mime type starts with
// "image/", an <img> tag can't render them — fall back to "download to view" instead.
function isBrowserRenderableImage(mimeType) {
  return mimeType?.startsWith('image/') && mimeType !== 'image/heic' && mimeType !== 'image/heif';
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'];

function fileExtension(name) {
  return (name?.split('.').pop() || '').toLowerCase();
}

// Small square preview: the real image for browser-renderable images, otherwise a
// format badge (PDF, DOC, XLS…) since PDFs and the like can't be shown as a thumbnail.
export function DocumentThumbnail({ doc, onClick }) {
  // Trust the extension too: stored mime types can be missing or generic (octet-stream).
  const previewable =
    isBrowserRenderableImage(doc.mime_type) || IMAGE_EXTENSIONS.includes(fileExtension(doc.original_name));
  const { data: blob } = useQuery({
    queryKey: ['document-thumb', doc.id],
    queryFn: async () => (await downloadDocument(doc.id)).data,
    enabled: previewable,
    staleTime: Infinity,
    retry: false,
  });
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!blob) return undefined;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  const ext = fileExtension(doc.original_name);
  const label = (ext || 'file').slice(0, 4).toUpperCase();
  const tone =
    ext === 'pdf' ? 'bg-red-50 text-red-600' : ['doc', 'docx'].includes(ext) ? 'bg-blue-50 text-blue-600' : ['xls', 'xlsx', 'csv'].includes(ext) ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500';

  return (
    <button
      type="button"
      onClick={onClick}
      title="Preview"
      className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden border-b border-gray-200 bg-gray-50"
    >
      {previewable && url ? (
        <img src={url} alt={doc.original_name} className="h-full w-full object-cover" />
      ) : (
        <span className={`flex h-full w-full flex-col items-center justify-center gap-0.5 ${tone}`}>
          <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
          </svg>
          <span className="text-xs font-bold leading-none">{label}</span>
        </span>
      )}
    </button>
  );
}

export function DocumentPreviewModal({ doc, onClose, onVerify, verifying, readOnly = false }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!doc) return undefined;

    let objectUrl;
    let cancelled = false;
    setStatus('loading');

    downloadDocument(doc.id)
      .then((response) => {
        if (cancelled) return;
        // The download endpoint sends an attachment response, so the blob's type can be generic
        // (octet-stream). Firefox then downloads instead of rendering — retype it explicitly.
        const ext = fileExtension(doc.original_name);
        let type = response.data.type;
        if (!type || type === 'application/octet-stream') {
          type = doc.mime_type && doc.mime_type !== 'application/octet-stream'
            ? doc.mime_type
            : ext === 'pdf' ? 'application/pdf' : IMAGE_EXTENSIONS.includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : type;
        }
        objectUrl = URL.createObjectURL(type && type !== response.data.type ? new Blob([response.data], { type }) : response.data);
        setPreviewUrl(objectUrl);
        setPreviewType(type);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc]);

  return (
    <Modal
      open={!!doc}
      onClose={onClose}
      title={doc?.original_name || 'Document preview'}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!readOnly && (
            <Button onClick={onVerify} loading={verifying}>
              Verify
            </Button>
          )}
        </>
      }
    >
      {doc && status === 'loading' && <p className="py-8 text-center text-sm text-gray-500">Loading preview…</p>}
      {doc && status === 'error' && <p className="py-8 text-center text-sm text-red-600">Could not load preview.</p>}
      {doc && status === 'ready' && isBrowserRenderableImage(previewType) && (
        <img src={previewUrl} alt={doc.original_name} className="mx-auto max-h-[70vh] max-w-full rounded-md" />
      )}
      {doc && status === 'ready' && previewType === 'application/pdf' && (
        // navpanes=0 tells the browser's built-in PDF viewer to keep its thumbnail/outline
        // sidebar closed by default, so it doesn't eat half the popup on multi-page PDFs.
        <object
          data={`${previewUrl}#toolbar=1&navpanes=0`}
          type="application/pdf"
          aria-label={doc.original_name}
          className="h-[75vh] w-full rounded-md border border-gray-200"
        >
          <p className="py-8 text-center text-sm text-gray-500">Preview not available in this browser — download to view it.</p>
        </object>
      )}
      {doc && status === 'ready' && previewType && !isBrowserRenderableImage(previewType) && previewType !== 'application/pdf' && (
        <p className="py-8 text-center text-sm text-gray-500">Preview not available for this file type — download to view it.</p>
      )}
    </Modal>
  );
}

export default function DocumentsTab({ candidate }) {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });

  const verifyMutation = useMutation({
    mutationFn: ({ id, verified }) => (verified ? unverifyDocument(id) : verifyDocument(id)),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Document updated.' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Document deleted.' });
      setDeleteTarget(null);
    },
  });

  const handleDownload = async (doc) => {
    try {
      const response = await downloadDocument(doc.id);
      saveBlobResponse(response, doc.original_name);
    } catch {
      pushToast({ type: 'error', message: 'Could not download document.' });
    }
  };

  // Most recently uploaded first.
  const documents = [...candidate.documents].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Can perm="documents.upload">
          <Button onClick={() => setUploadOpen(true)}>Upload document</Button>
        </Can>
      </div>

      {documents.length === 0 ? (
        <EmptyState title="No documents uploaded yet." />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {documents.map((doc) => (
            <div key={doc.id} className="flex flex-col overflow-hidden rounded-md border border-gray-200">
              <div className="relative">
                <DocumentThumbnail doc={doc} onClick={() => setViewDoc(doc)} />
                <span
                  className={`absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    doc.is_verified ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {doc.is_verified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-1 p-2">
                <p className="truncate text-xs font-medium text-gray-900" title={doc.original_name}>
                  {doc.original_name}
                </p>
                <p className="truncate text-[11px] text-gray-500" title={doc.document_type_name}>
                  {doc.document_type_name}
                  {formatSize(doc.size) && ` · ${formatSize(doc.size)}`}
                </p>
                <p className="text-[11px] text-gray-400">
                  {new Date(doc.created_at).toLocaleDateString()}
                  {doc.expiry_date && ` · exp ${new Date(doc.expiry_date).toLocaleDateString()}`}
                </p>
                <div className="mt-auto flex flex-wrap gap-x-3 gap-y-0.5 pt-1 text-xs">
                  <button onClick={() => handleDownload(doc)} className="text-primary hover:underline">
                    Download
                  </button>
                  {can('documents.verify') && (
                    <button
                      onClick={() => (doc.is_verified ? verifyMutation.mutate({ id: doc.id, verified: true }) : setPreviewDoc(doc))}
                      className="text-primary hover:underline"
                    >
                      {doc.is_verified ? 'Unverify' : 'Verify'}
                    </button>
                  )}
                  <Can perm="documents.delete">
                    <button onClick={() => setDeleteTarget(doc)} className="text-red-600 hover:underline">
                      Delete
                    </button>
                  </Can>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} candidateId={candidate.id} />

      <DocumentPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        verifying={verifyMutation.isPending}
        onVerify={() =>
          verifyMutation.mutate(
            { id: previewDoc.id, verified: false },
            { onSuccess: () => setPreviewDoc(null) }
          )
        }
      />

      <DocumentPreviewModal doc={viewDoc} onClose={() => setViewDoc(null)} readOnly />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete document"
        message={`Delete ${deleteTarget?.original_name}?`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
